/** Day vs. Depth vs. Cost, beside the daily drilling summary. */

const W = 640;
const H = 380;
const PAD = { top: 34, right: 78, bottom: 30, left: 54 };
const MAX_DAYS = 80;
const MAX_DEPTH = 3600;
const MAX_COST = 165;

const x = (d: number) => PAD.left + (d / MAX_DAYS) * (W - PAD.left - PAD.right);
const yDepth = (m: number) => PAD.top + (m / MAX_DEPTH) * (H - PAD.top - PAD.bottom);
const yCost = (m: number) => H - PAD.bottom - (m / MAX_COST) * (H - PAD.top - PAD.bottom);

const DEPTH_TRACK: [number, number][] = [
  [2, 120],
  [6, 480],
  [8, 300],
  [11, 980],
  [14, 1180],
  [18, 1150],
  [30, 1900],
  [36, 2100],
  [41, 2380],
  [46, 2600],
  [52, 2900],
  [58, 3050],
  [63, 3120],
  [66, 2650],
  [70, 300],
  [74, 120],
];

const COST_TRACK: [number, number][] = [
  [44, 18],
  [50, 30],
  [56, 44],
  [62, 58],
  [68, 72],
  [74, 86],
];

const PLANNED_TOP: [number, number][] = [
  [44, 60],
  [52, 60],
  [58, 60],
  [64, 60],
];

const NOTES = [
  { d: 12, m: 700, text: 'Drill 8-1/2" Pilot Hole' },
  { d: 26, m: 2150, text: 'Drill 17-1/2" x 20" Hole' },
  { d: 30, m: 2320, text: 'Drill 12-1/4" x 14" Hole' },
  { d: 33, m: 2480, text: 'Drill 10-5/8" x 12-1/4" Hole' },
  { d: 40, m: 2760, text: 'Drill 8-1/2" Hole' },
];

const GREEN_NOTES = [
  { d: 30, m: 1240, text: 'Continue Drill 8-1/2" Pilot Hole to 998m MDDF – in progress.' },
  { d: 30, m: 1420, text: 'Continue to RIH 24" Rotary BHA to 337m MDDF – in progress.' },
  { d: 34, m: 1560, text: 'Drill 17½" x 20" hole to 434m MDDF – current depth.' },
  { d: 36, m: 1720, text: 'Changing out Bearing Assembly in RCD housing.' },
];

const SUMMARY: [string, string][] = [
  ['Report Number', '85'],
  ['Report Date', '10/09/2026'],
];

export function WellProgress() {
  return (
    <div className="splitpane">
      <section className="panel panel--pad" aria-label="Day versus depth versus cost">
        <header className="progresshead">
          <h2>Day vs. Depth vs. Cost</h2>
          <button type="button" className="btn-series">
            <svg viewBox="0 0 16 16" width="13" height="13" aria-hidden="true">
              <path
                d="M1.5 12.5 5 8l3 2.5 4.5-6"
                fill="none"
                stroke="currentColor"
                strokeWidth="1.6"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
            Data Series
          </button>
          <span className="progresshead-stamp">🕐 11/09/2026 05:22</span>
        </header>

        <svg
          className="progresschart"
          viewBox={`0 0 ${W} ${H}`}
          role="img"
          aria-label="Drilling days against hole depth and cumulative cost"
        >
          <text x={W / 2} y={16} className="pc-axis-title">
            DAYS
          </text>
          <text x={20} y={H / 2} className="pc-axis-title" transform={`rotate(-90 20 ${H / 2})`}>
            HOLE DEPTH (m)
          </text>
          <text
            x={W - 16}
            y={H / 2}
            className="pc-axis-title"
            transform={`rotate(-90 ${W - 16} ${H / 2})`}
          >
            COST (USD)
          </text>

          <text x={PAD.left} y={PAD.top - 6} className="pc-start">
            20/07/2026
          </text>

          {[15, 30, 45, 60, 75].map((d) => (
            <g key={d}>
              <line x1={x(d)} x2={x(d)} y1={PAD.top} y2={H - PAD.bottom} stroke="#eef1f3" />
              <text x={x(d)} y={PAD.top - 6} className="pc-tick">
                {d}
              </text>
            </g>
          ))}

          {[1000, 2000, 3000].map((m) => (
            <text key={m} x={PAD.left - 8} y={yDepth(m) + 3} className="pc-tick pc-tick--y">
              {m / 1000}k
            </text>
          ))}

          {[60, 90, 120, 150].map((c) => (
            <text key={c} x={W - PAD.right + 8} y={yCost(c) + 3} className="pc-tick pc-tick--y2">
              {c}M
            </text>
          ))}

          <path
            d={DEPTH_TRACK.map(([d, m], i) => `${i ? 'L' : 'M'}${x(d)},${yDepth(m)}`).join(' ')}
            fill="none"
            stroke="#1b1b1b"
            strokeWidth="1.4"
          />
          {DEPTH_TRACK.map(([d, m]) => (
            <circle key={`${d}-${m}`} cx={x(d)} cy={yDepth(m)} r="2.6" fill="#1b1b1b" />
          ))}

          <path
            d={PLANNED_TOP.map(([d, m], i) => `${i ? 'L' : 'M'}${x(d)},${yCost(m)}`).join(' ')}
            fill="none"
            stroke="#2e9e4f"
            strokeWidth="2.4"
          />
          {PLANNED_TOP.map(([d, m]) => (
            <circle key={`p${d}`} cx={x(d)} cy={yCost(m)} r="3" fill="#2e9e4f" />
          ))}

          <path
            d={COST_TRACK.map(([d, c], i) => `${i ? 'L' : 'M'}${x(d)},${yCost(c)}`).join(' ')}
            fill="none"
            stroke="#d03b34"
            strokeWidth="1.6"
          />
          {COST_TRACK.map(([d, c]) => (
            <path
              key={`c${d}`}
              d={`M${x(d)},${yCost(c) - 3.4} L${x(d) + 3.4},${yCost(c) + 2.6} L${x(d) - 3.4},${yCost(c) + 2.6} Z`}
              fill="#d03b34"
            />
          ))}

          <text x={x(2) + 4} y={yDepth(60)} className="pc-note">
            Start of Well
          </text>
          <text x={x(62)} y={yDepth(120)} className="pc-note">
            P&amp;A  Rig Move
          </text>

          {NOTES.map((n) => (
            <text key={n.text} x={x(n.d)} y={yDepth(n.m)} className="pc-note">
              {n.text}
            </text>
          ))}

          {GREEN_NOTES.map((n) => (
            <text key={n.text} x={x(n.d)} y={yDepth(n.m)} className="pc-note pc-note--green">
              {n.text}
            </text>
          ))}
        </svg>
      </section>

      <section className="panel panel--pad" aria-label="Daily drilling summary">
        <h2 className="summary-title">Daily Drilling Summary</h2>

        <dl className="summaryform">
          {SUMMARY.map(([k, v]) => (
            <div className="summaryrow" key={k}>
              <dt>{k}</dt>
              <dd>
                <span className="field-ro">{v}</span>
              </dd>
            </div>
          ))}

          <div className="summaryrow">
            <dt>Depth at Report Time</dt>
            <dd className="summaryrow--pair">
              <span className="field-ro">2,673</span>
              <em>mMD / 2,672.09</em>
              <span className="field-ro" />
              <em>mTVD</em>
            </dd>
          </div>

          <div className="summaryrow">
            <dt>Progress Depth</dt>
            <dd className="summaryrow--pair">
              <span className="field-ro" />
              <em>m</em>
            </dd>
          </div>
        </dl>

        <h3 className="summary-sub">Operation at Report Time</h3>
        <p className="summary-body">Full BOP Test - ongoing.</p>

        <h3 className="summary-sub">Last 24-hour Operation</h3>
        <p className="summary-body summary-body--boxed">
          Continue Wireline Run #3 (MDT). POOH &amp; L/D toolstring. Change out new saver sub. M/U
          with TDS and perform torque verification with Exlog and Driller&rsquo;s HMI - observe
          correct torque indication. Change out RCD Bearing Element. Retrieve Bore Protector to
          surface. Retrieve Wear Bushing. Jet clean wellhead, BOP, RCD housing. RIH and set BOP Test
          Plug. Unscrew string from BOP test plug and displace BOP stack with SW.
        </p>

        <h3 className="summary-sub">24-hour Forecast</h3>
        <p className="summary-body">
          Full BOP Test. RIH and set RCD test plug. Pressure test RCD. Retrieve RCD test plug and BOP
          test plug.
        </p>
      </section>
    </div>
  );
}
